/**
 * PremiumLoadingSkeleton - Componente genérico de skeleton loading
 * Reutilizável em todas as telas CRUD
 *
 * Props:
 * - rows: number (quantidade de linhas skeleton na tabela)
 * - columns: Array de objetos com configuração de cada coluna
 *   - { type: 'checkbox' | 'avatar' | 'text' | 'textShort' | 'textMedium' | 'chip' | 'chipSmall' | 'icon' | 'actions' | 'indicators', width?: number, align?: string, count?: number }
 *
 * Stats Props (página completa):
 * - showStats: boolean (mostrar skeleton dos cards de estatísticas)
 * - statsCount: number (quantidade de cards - default: 4)
 *
 * Filters Props:
 * - showFilters: boolean (mostrar skeleton dos filtros)
 * - showGroupFilter: boolean (mostrar filtro de grupo)
 *
 * Table Props:
 * - showTable: boolean (mostrar skeleton da tabela - default: true)
 * - wrapInPaper: boolean (envolver tabela em Paper - default: false, usado em página completa)
 */

import React from 'react';
import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { usePremiumTokens } from '../../theme/usePremiumTokens';

// Tipos de skeleton pré-definidos
const SkeletonTypes = {
  checkbox: { variant: 'rectangular', width: 20, height: 20 },
  avatar: { variant: 'circular', width: 32, height: 32 },
  avatarSmall: { variant: 'circular', width: 28, height: 28 },
  text: { variant: 'text', width: '60%' },
  textShort: { variant: 'text', width: 40 },
  textMedium: { variant: 'text', width: '40%' },
  textLong: { variant: 'text', width: '80%' },
  chip: { variant: 'rounded', width: 80, height: 22, borderRadius: 2 },
  chipSmall: { variant: 'rounded', width: 50, height: 22, borderRadius: 2 },
  icon: { variant: 'circular', width: 28, height: 28 },
  iconSmall: { variant: 'circular', width: 24, height: 24 },
  indicators: { variant: 'rounded', width: 100, height: 24, borderRadius: 1 },
};

// Componente de Skeleton para Stats Cards
const StatsSkeleton = ({ count = 4, tokens }) => (
  <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap' }}>
    {Array.from({ length: count }).map((_, index) => (
      <Skeleton
        key={index}
        variant="rounded"
        sx={{
          flex: { xs: '1 1 calc(50% - 4px)', sm: '1 1 120px' },
          maxWidth: { xs: 'calc(50% - 4px)', sm: 200 },
          height: 70,
          bgcolor: tokens?.surface?.elevated,
          borderRadius: tokens?.radius?.lg,
        }}
      />
    ))}
  </Box>
);

// Componente de Skeleton para Filtros
const FiltersSkeleton = ({ showGroupFilter = false, tokens }) => (
  <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap' }}>
    {/* Search field */}
    <Skeleton
      variant="rounded"
      sx={{
        flex: { xs: 1, sm: 2, md: 3 },
        minWidth: { xs: '100%', sm: 200 },
        height: 40,
        bgcolor: tokens?.surface?.elevated,
        borderRadius: tokens?.radius?.md,
      }}
    />
    {/* Group filter */}
    {showGroupFilter && (
      <Skeleton
        variant="rounded"
        sx={{
          flex: { xs: 1, sm: 1 },
          minWidth: { xs: '100%', sm: 180 },
          maxWidth: { sm: 250 },
          height: 40,
          bgcolor: tokens?.surface?.elevated,
          borderRadius: tokens?.radius?.md,
        }}
      />
    )}
    {/* Action buttons */}
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Skeleton
        variant="rounded"
        width={36}
        height={36}
        sx={{ bgcolor: tokens?.surface?.elevated, borderRadius: tokens?.radius?.md }}
      />
      <Skeleton
        variant="rounded"
        width={100}
        height={36}
        sx={{ bgcolor: tokens?.surface?.elevated, borderRadius: tokens?.radius?.md }}
      />
      <Skeleton
        variant="rounded"
        width={90}
        height={36}
        sx={{ bgcolor: tokens?.surface?.elevated, borderRadius: tokens?.radius?.md }}
      />
    </Box>
  </Box>
);

// Componente principal - Skeleton de linhas da tabela
const TableRowsSkeleton = ({ rows, columns, tokens, actionsCount }) => {
  const renderSkeletonCell = (config, index) => {
    const { type, width, height, align, count } = config;

    // Tipo checkbox
    if (type === 'checkbox') {
      return (
        <TableCell key={index} padding="checkbox" sx={{ py: 1, width: 48 }}>
          <Skeleton
            variant="rectangular"
            width={20}
            height={20}
            sx={{ bgcolor: tokens?.surface?.elevated }}
          />
        </TableCell>
      );
    }

    // Tipo actions - renderiza múltiplos botões
    if (type === 'actions') {
      const actionCount = count || actionsCount;
      return (
        <TableCell key={index} align={align || 'right'} sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: align || 'flex-end', gap: 0.5 }}>
            {Array.from({ length: actionCount }).map((_, i) => (
              <Skeleton
                key={i}
                variant="circular"
                width={28}
                height={28}
                sx={{ bgcolor: tokens?.surface?.elevated }}
              />
            ))}
          </Box>
        </TableCell>
      );
    }

    // Tipo indicators (ignição, bloqueio, velocidade)
    if (type === 'indicators') {
      return (
        <TableCell key={index} align="center" sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: tokens?.surface?.elevated }} />
            <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: tokens?.surface?.elevated }} />
            <Skeleton variant="rounded" width={42} height={24} sx={{ bgcolor: tokens?.surface?.elevated }} />
          </Box>
        </TableCell>
      );
    }

    // Tipo composto: avatar + texto (nome + subtítulo)
    if (type === 'avatarWithText') {
      return (
        <TableCell key={index} sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: tokens?.surface?.elevated }} />
            <Box>
              <Skeleton variant="text" width={120} sx={{ bgcolor: tokens?.surface?.elevated }} />
              <Skeleton variant="text" width={80} height={12} sx={{ bgcolor: tokens?.surface?.elevated }} />
            </Box>
          </Box>
        </TableCell>
      );
    }

    // Outros tipos pré-definidos
    const preset = SkeletonTypes[type] || SkeletonTypes.text;
    const skeletonWidth = width || preset.width;
    const skeletonHeight = height || preset.height;

    // Para chips e elementos centralizados
    if (align === 'center' || type === 'chip' || type === 'chipSmall') {
      return (
        <TableCell key={index} align="center" sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Skeleton
              variant={preset.variant}
              width={skeletonWidth}
              height={skeletonHeight}
              sx={{
                bgcolor: tokens?.surface?.elevated,
                borderRadius: preset.borderRadius,
              }}
            />
          </Box>
        </TableCell>
      );
    }

    return (
      <TableCell key={index} align={align} sx={{ py: 1 }}>
        <Skeleton
          variant={preset.variant}
          width={skeletonWidth}
          height={skeletonHeight}
          sx={{
            bgcolor: tokens?.surface?.elevated,
            borderRadius: preset.borderRadius,
          }}
        />
      </TableCell>
    );
  };

  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <TableRow key={rowIndex}>
          {columns.map((config, colIndex) => renderSkeletonCell(config, colIndex))}
        </TableRow>
      ))}
    </>
  );
};

// Componente principal
const PremiumLoadingSkeleton = ({
  // Table props
  rows = 5,
  columns,
  showAvatar = true,
  showActions = true,
  actionsCount = 2,
  showTable = true,
  wrapInPaper = false,

  // Stats props
  showStats = false,
  statsCount = 4,

  // Filters props
  showFilters = false,
  showGroupFilter = false,

  // Header columns para skeleton do header
  headerColumns,
}) => {
  const tokens = usePremiumTokens();

  // Gera configuração de colunas padrão se não fornecida
  const getDefaultColumns = () => {
    const cols = [];
    if (showAvatar) {
      cols.push({ type: 'avatar' });
    }
    cols.push({ type: 'textShort' }); // ID
    cols.push({ type: 'text' }); // Nome/Descrição
    cols.push({ type: 'chip' }); // Status/Tipo
    if (showActions) {
      cols.push({ type: 'actions', count: actionsCount });
    }
    return cols;
  };

  const columnConfig = columns || getDefaultColumns();

  // Se apenas rows da tabela (uso dentro de TableBody existente)
  if (!showStats && !showFilters && !wrapInPaper) {
    return (
      <TableRowsSkeleton
        rows={rows}
        columns={columnConfig}
        tokens={tokens}
        actionsCount={actionsCount}
      />
    );
  }

  // Renderização completa de página
  const tableContent = showTable && (
    <Table>
      {headerColumns && (
        <TableHead>
          <TableRow>
            {headerColumns.map((col, i) => (
              <TableCell key={i} width={col.width} align={col.align}>
                <Skeleton
                  variant="text"
                  width={col.labelWidth || 60}
                  sx={{ bgcolor: tokens?.surface?.elevated }}
                />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        <TableRowsSkeleton
          rows={rows}
          columns={columnConfig}
          tokens={tokens}
          actionsCount={actionsCount}
        />
      </TableBody>
    </Table>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Stats Skeleton */}
      {showStats && <StatsSkeleton count={statsCount} tokens={tokens} />}

      {/* Filters Skeleton */}
      {showFilters && <FiltersSkeleton showGroupFilter={showGroupFilter} tokens={tokens} />}

      {/* Table Skeleton */}
      {showTable && (
        wrapInPaper ? (
          <Paper
            sx={{
              backgroundColor: tokens?.surface?.glass,
              borderRadius: tokens?.radius?.lg,
              border: `1px solid ${tokens?.border?.subtle}`,
              overflow: 'hidden',
            }}
          >
            {tableContent}
          </Paper>
        ) : (
          tableContent
        )
      )}
    </Box>
  );
};

export default React.memo(PremiumLoadingSkeleton);
